/* ================================================================
   InlineFormatToolbar — utility functions for rich-text formatting
   ================================================================
   פונקציות עזר לעיצוב טקסט inline (Bold/Underline/Bullets) בתוך
   אלמנטי contentEditable.

   הסרגל הצף הוסר — כל הכפתורים משולבים עכשיו בפאנל "עיצוב טקסט"
   (TypographyPanel) שבתוך EditableText.
   ================================================================ */

import type React from "react";

/** Execute a formatting command on the current selection */
export function execFormat(command: string, _value?: string) {
  document.execCommand(command, false, _value);
}

/** Check if a formatting command is active for the current selection */
export function isFormatActive(command: string): boolean {
  try {
    return document.queryCommandState(command);
  } catch {
    return false;
  }
}

/* ================================================================
   handleFormatKeyDown — keyboard shortcuts for contentEditable
   ================================================================
   מטפל ב-Ctrl+B ו-Ctrl+U בתוך אלמנט contentEditable.
   יש לקרוא ל-handler הזה מתוך onKeyDown של האלמנט.
   ================================================================ */

export function handleFormatKeyDown(e: React.KeyboardEvent): boolean {
  const ctrlOrMeta = e.ctrlKey || e.metaKey;
  if (!ctrlOrMeta) return false;

  if (e.key === "b" || e.key === "B") {
    e.preventDefault();
    execFormat("bold");
    return true;
  }
  if (e.key === "u" || e.key === "U") {
    e.preventDefault();
    execFormat("underline");
    return true;
  }
  return false;
}

/* ================================================================
   extractRichValue — extract innerHTML preserving formatting tags
   ================================================================
   שומר על תגיות עיצוב (b, strong, u, em, ul, ol, li, br)
   ומסיר את כל השאר (div wrappers, span wrappers etc.)
   ================================================================ */

const ALLOWED_TAGS_SET = new Set([
  "b", "strong", "u", "em", "i", "ul", "ol", "li", "br",
]);

/** Clean innerHTML: keep formatting tags, strip the rest, normalize whitespace */
export function extractRichValue(el: HTMLElement): string {
  const html = el.innerHTML;

  // If there are no HTML tags at all, just return text
  if (!/<[^>]+>/i.test(html)) {
    return (el.textContent || "").replace(/\u200B/g, "").trim();
  }

  // Step 1: normalize div/p line breaks to <br>
  let cleaned = html
    .replace(/<div><br\s*\/?><\/div>/gi, "<br>")
    .replace(/<\/div>\s*<div>/gi, "<br>")
    .replace(/<\/?div>/gi, "<br>")
    .replace(/<\/p>\s*<p>/gi, "<br>")
    .replace(/<\/?p>/gi, "");

  // Step 2: strip disallowed tags but keep their content
  cleaned = cleaned.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/gi, (match, tagName) => {
    const tag = tagName.toLowerCase();
    if (ALLOWED_TAGS_SET.has(tag)) return match.replace(/\s+style="[^"]*"/gi, ""); // keep tag but strip inline styles
    return "";
  });

  // Step 3: clean up
  cleaned = cleaned
    .replace(/\u200B/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/(<br\s*\/?>){3,}/gi, "<br><br>")
    .replace(/^\s*(<br\s*\/?>)+/i, "")
    .replace(/(<br\s*\/?>)+\s*$/i, "")
    .trim();

  // If result is empty or only whitespace/br tags, return empty
  if (!cleaned || /^(<br\s*\/?>|\s)*$/i.test(cleaned)) return "";

  // If result has no formatting tags, return as plain text
  const hasFormatting = /<(b|strong|u|em|i|ul|ol|li)\b/i.test(cleaned);
  if (!hasFormatting) {
    // Convert br to newlines and strip remaining tags
    const plain = cleaned
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    return plain;
  }

  return cleaned;
}

/* ================================================================
   hasHtmlFormatting — check if saved content contains HTML tags
   ================================================================ */

export function hasHtmlFormatting(text: string): boolean {
  return /<(b|strong|u|em|i|ul|ol|li)\b/i.test(text);
}